import type {StoreApi} from 'zustand/vanilla';

import type {EagGridState} from '../store/createEagGridStore';

/**
 * Resolves on the FIRST transition into a settled fetch state after this call.
 *
 * Deliberately ignores the CURRENT value: an apply dispatched while the grid sits
 * in 'success' from the previous query must wait for the new query's fetch, not
 * report the old one. Resolves 'timeout' rather than hanging — a fetch superseded
 * by a newer one (owner transfer in GridDataEngine) may never settle at all.
 */
export const waitForFetchSettle = (
  store: StoreApi<EagGridState>,
  timeoutMs: number
): Promise<'success' | 'error' | 'timeout'> =>
  new Promise((resolve) => {
    // Swapped for the real unsubscribe the moment the subscription exists. Both
    // closures below only ever run afterwards — a zustand listener cannot fire
    // during subscribe(), and the timeout is a later macrotask.
    let unsubscribe: () => void = () => undefined;

    const timeoutId = setTimeout(() => {
      unsubscribe();
      resolve('timeout');
    }, timeoutMs);

    unsubscribe = store.subscribe((state, prevState) => {
      if (state.fetchState === prevState.fetchState) {
        return;
      }

      if (state.fetchState === 'success' || state.fetchState === 'error') {
        clearTimeout(timeoutId);
        unsubscribe();
        resolve(state.fetchState);
      }
    });
  });
